<script>
  import { onMount } from 'svelte';
  import PopupMap from "./PopupMap.svelte";

  export let tour;
  export let map;
  export let isVisible = false;

  let selectedIndex = -1;
  let items = [];
  let panelElement;
  let filterText = '';
  let searchInput;
  
  // Focus search input when menu becomes visible
  $: if (isVisible) {
    setTimeout(() => {
      searchInput?.focus();
    }, 0);
  }

  // Combine POIs and buildings into a single accessible list
  $: {
    items = [];
    
    // Add POIs if they exist
    if (tour?.mapbox?.pois) {
      items.push(...tour.mapbox.pois.filter(poi => !poi.hidden).map(poi => ({
        type: 'poi',
        title: poi.title,
        description: poi.description,
        data: poi
      })));
    }

    // Add numeric POIs if they exist
    if (tour?.mapbox?.numericPois) {
      items.push(...tour.mapbox.numericPois.filter(poi => !poi.hidden).map((poi, index) => ({
        type: 'numericPoi',
        title: `${index + 1}. ${poi.title}`,
        description: poi.description,
        data: poi
      })));
    }

    // Add buildings if they exist
    if (tour?.mapbox?.buildings) {
      items.push(...tour.mapbox.buildings.filter(building => !building.hidden).map(building => ({
        type: 'building',
        title: building.title,
        description: building.popupText,
        data: building
      })));
    }

    // Add polygon POIs if they exist
    if (tour?.mapbox?.polygonPois) {
      items.push(...tour.mapbox.polygonPois.filter(poi => !poi.hidden).map(poi => ({
        type: 'polygonPoi',
        title: poi.title,
        description: poi.description,
        data: poi
      })));
    }
  }

  // Group items by type
  $: groupedItems = {
    pois: items.filter(item => item.type === 'poi'),
    numericPois: items.filter(item => item.type === 'numericPoi'),
    buildings: items.filter(item => item.type === 'building'),
    polygonPois: items.filter(item => item.type === 'polygonPoi')
  };

  // Add filtered items reactive declaration
  $: filteredGroupedItems = {
    pois: groupedItems.pois.filter(item => 
      item.title.toLowerCase().includes(filterText.toLowerCase())
    ),
    numericPois: groupedItems.numericPois.filter(item => 
      item.title.toLowerCase().includes(filterText.toLowerCase())
    ),
    buildings: groupedItems.buildings.filter(item => 
      item.title.toLowerCase().includes(filterText.toLowerCase())
    ),
    polygonPois: groupedItems.polygonPois.filter(item => 
      item.title.toLowerCase().includes(filterText.toLowerCase())
    )
  };

  // Update flattened items to use filtered groups
  $: filteredFlattenedItems = [
    ...filteredGroupedItems.pois,
    ...filteredGroupedItems.numericPois,
    ...filteredGroupedItems.buildings,
    ...filteredGroupedItems.polygonPois
  ];

  // When filter text changes, reset selection
  $: {
    filterText;
    selectedIndex = -1;
  }

  function focusItem(index) {
    selectedIndex = index;
    const buttons = panelElement?.querySelectorAll('.item-button');
    if (buttons?.[index]) {
      buttons[index].focus();
    }
  }

  function handleKeydown(event) {
    const popup = document.querySelector('.popup');
    if (event.key === 'Escape') {
      if (popup) {
        // If there's a popup, let it handle its own closing
        // But after it's closed, refocus back to our menu
        setTimeout(() => {
          if (isVisible) {
            searchInput?.focus();
          }
        }, 0);
      } else if (isVisible) {
        // No popup, so we can close our menu
        event.preventDefault();
        // clear the filter text
        filterText = '';
        isVisible = false;
        // Return focus to the accessibility button
        document.getElementById('accessibility-tour-button')?.focus();
      }
    }
  }

  // Add a watcher to sync isVisible changes back to parent
  $: if (!isVisible) {
    // Dispatch an event to notify parent of closure
    const event = new CustomEvent('close');
    panelElement?.dispatchEvent(event);
  }

  function handleItemSelect(item) {
    // Fly to the location
    if (item.type === 'polygonPoi') {
      // Fly to the polygon POI center
      if (item.data.coordinates && item.data.coordinates.length >= 3) {
        const coords = item.data.coordinates;
        let sumLng = 0, sumLat = 0, count = 0;
        
        // Handle nested coordinate format [[lng, lat], [lng, lat], ...]
        coords.forEach(coord => {
          sumLng += coord[0];
          sumLat += coord[1];
          count++;
        });
        
        if (count > 0) {
          const center = [sumLng / count, sumLat / count];
          map.flyTo({
            center: center,
            zoom: 16,
          });
        }
      }
    } else if (item.data.location) {
      map.flyTo({
        center: item.data.location,
        zoom: 18,
      });
    }

    // Show popup if available
    const popup = {
      title: item.title,
      content: item.description,
      linkedscenes: item.data.sceneIds?.map(sceneId => {
        const scene = tour?.scenes?.find(scene => scene.id === sceneId);
        return {
          id: sceneId,
          name: scene?.name,
          title: scene?.title
        };
      }),
      directions: item.data.location
    };

    if (popup.title || popup.content || popup.linkedscenes?.length) {
      new PopupMap({
        target: document.body,
        props: popup
      });
    }
  }

  function handleItemFocus(item) {
    // Fly to the building
    if (item.type === 'building') {
      // Get the building's feature ID
      const featureId = item.data.featureId;
      if (featureId) {
        // Get the building's feature
        const features = map.querySourceFeatures('base-buildings', {
          sourceLayer: tour?.mapbox?.mapboxTilesetLayerName,
          filter: ['==', ['get', '@id'], featureId]
        });
        
        if (features.length > 0) {
          // Calculate the building's center
          const coordinates = features[0].geometry.coordinates[0];
          const center = coordinates.reduce((acc, coord) => {
            return [acc[0] + coord[0], acc[1] + coord[1]];
          }, [0, 0]).map(sum => sum / coordinates.length);
          // Fly away
          map.flyTo({
            center: center,
            zoom: 18,
          });
        }
      }
    } else if (item.type === 'polygonPoi') {
      // Fly to the polygon POI center
      if (item.data.coordinates && item.data.coordinates.length >= 3) {
        // Calculate center from polygon coordinates
        const coords = item.data.coordinates;
        let sumLng = 0, sumLat = 0, count = 0;
        
        // Handle nested coordinate format [[lng, lat], [lng, lat], ...]
        coords.forEach(coord => {
          sumLng += coord[0];
          sumLat += coord[1];
          count++;
        });
        
        if (count > 0) {
          const center = [sumLng / count, sumLat / count];
          map.flyTo({
            center: center,
            zoom: 16,
          });
        }
      }
    } else if (item.data.location) {
      // Fly to the POI
      map.flyTo({
        center: item.data.location,
        zoom: 18,
      });
    }
  }

  onMount(() => {
    window.addEventListener('keydown', handleKeydown);
    return () => {
      window.removeEventListener('keydown', handleKeydown);
    };
  });
</script>

{#if isVisible}
  <div 
    class="accessibility-panel"
    role="dialog"
    aria-label="Map locations"
    bind:this={panelElement}
  >
    <div class="header">
      <h2 id="location-heading">Map Locations</h2>
      <button 
        class="close-button" 
        aria-label="Close locations panel"
        on:click={() => isVisible = false}
      >
        ×
      </button>
    </div>
    <div class="search">
      <input
        type="text"
        placeholder="Type to filter locations..."
        bind:value={filterText}
        bind:this={searchInput}
        aria-label="Filter locations"
      >
    </div>
    <div 
      class="content"
      role="application"
      aria-labelledby="location-heading"
    >
      {#if filteredGroupedItems.pois.length > 0}
        <div 
          class="group"
          role="region"
          aria-labelledby="poi-heading"
        >
          <h3 id="poi-heading" class="group-heading">Points of Interest</h3>
          <div role="listbox" aria-labelledby="poi-heading">
            {#each filteredGroupedItems.pois as item, index}
              <button
                class="item-button"
                class:selected={filteredFlattenedItems.indexOf(item) === selectedIndex}
                on:click={() => handleItemSelect(item)}
                on:focus={() => handleItemFocus(item)}
                aria-selected={filteredFlattenedItems.indexOf(item) === selectedIndex}
                role="option"
              >
                {item.title}
              </button>
            {/each}
          </div>
        </div>
      {/if}

      {#if filteredGroupedItems.numericPois.length > 0}
        <div 
          class="group"
          role="region"
          aria-labelledby="numeric-heading"
        >
          <h3 id="numeric-heading" class="group-heading">Numbered Locations</h3>
          <div role="listbox" aria-labelledby="numeric-heading">
            {#each filteredGroupedItems.numericPois as item, index}
              <button
                class="item-button"
                class:selected={filteredFlattenedItems.indexOf(item) === selectedIndex}
                on:click={() => handleItemSelect(item)}
                on:focus={() => handleItemFocus(item)}
                aria-selected={filteredFlattenedItems.indexOf(item) === selectedIndex}
                role="option"
              >
                {item.title}
              </button>
            {/each}
          </div>
        </div>
      {/if}

      {#if filteredGroupedItems.buildings.length > 0}
        <div 
          class="group"
          role="region"
          aria-labelledby="building-heading"
        >
          <h3 id="building-heading" class="group-heading">Buildings</h3>
          <div role="listbox" aria-labelledby="building-heading">
            {#each filteredGroupedItems.buildings as item, index}
              <button
                class="item-button"
                class:selected={filteredFlattenedItems.indexOf(item) === selectedIndex}
                on:click={() => handleItemSelect(item)}
                on:focus={() => handleItemFocus(item)}
                aria-selected={filteredFlattenedItems.indexOf(item) === selectedIndex}
                role="option"
              >
                {item.title}
              </button>
            {/each}
          </div>
        </div>
      {/if}

      {#if filteredGroupedItems.polygonPois.length > 0}
        <div 
          class="group"
          role="region"
          aria-labelledby="polygon-heading"
        >
          <h3 id="polygon-heading" class="group-heading">Areas</h3>
          <div role="listbox" aria-labelledby="polygon-heading">
            {#each filteredGroupedItems.polygonPois as item, index}
              <button
                class="item-button"
                class:selected={filteredFlattenedItems.indexOf(item) === selectedIndex}
                on:click={() => handleItemSelect(item)}
                on:focus={() => handleItemFocus(item)}
                aria-selected={filteredFlattenedItems.indexOf(item) === selectedIndex}
                role="option"
              >
                {item.title}
              </button>
            {/each}
          </div>
        </div>
      {/if}

      {#if filteredFlattenedItems.length === 0}
        <div class="no-results">
          No locations match your search
        </div>
      {/if}
    </div>
  </div>
{/if}

<style>
  .accessibility-panel {
    position: absolute;
    top: 90px;
    right: 20px;
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    width: 300px;
    max-height: calc(100vh - 40px);
    z-index: 1000;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .header {
    padding: 15px;
    background: #f5f5f5;
    border-bottom: 1px solid #eee;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .header h2 {
    margin: 0;
    font-size: 1.2em;
    color: #333;
  }

  .content {
    overflow-y: auto;
    max-height: 60vh;
  }

  .group {
    border-bottom: 2px solid #eee;
  }

  .group:last-child {
    border-bottom: none;
  }

  .group-heading {
    margin: 0;
    padding: 12px 15px;
    background: #f8f8f8;
    font-size: 1.1em;
    color: #444;
    font-weight: 600;
  }

  .item-button {
    width: 100%;
    text-align: left;
    background: none;
    border: none;
    padding: 10px 15px;
    cursor: pointer;
    border-bottom: 1px solid #eee;
    color: #333;
    font-size: 14px;
  }

  .item-button:last-child {
    border-bottom: none;
  }

  .item-button:hover {
    background: #f5f5f5;
  }

  .item-button:focus {
    outline: none;
    box-shadow: inset 0 0 0 4px var(--focus-color);
  }

  .item-button.selected {
    background: #e3f2fd;
    box-shadow: inset 0 0 0 2px #2196f3;
  }

  .close-button {
    background: none;
    border: none;
    font-size: 24px;
    line-height: 1;
    padding: 4px 8px;
    cursor: pointer;
    color: #666;
    border-radius: 4px;
  }

  .close-button:hover {
    background: #e0e0e0;
    color: #333;
  }

  .close-button:focus {
    outline: none;
    box-shadow: 0 0 0 2px #2196f3;
    background: #e0e0e0;
    color: #333;
  }

  .search {
    padding: 10px 15px;
    border-bottom: 1px solid #eee;
  }

  .search input {
    width: 100%;
    padding: 6px 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
    box-sizing: border-box;
  }

  .search input:focus {
    outline: none;
    border-color: #2196f3;
    box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.2);
  }

  .no-results {
    padding: 20px;
    text-align: center;
    color: #666;
    font-style: italic;
  }
</style> 